import { Field, FieldArray, FormikValues } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from 'reactstrap/lib/Card';
import CardBody from 'reactstrap/lib/CardBody';
import Col from 'reactstrap/lib/Col';
import FormGroup from 'reactstrap/lib/FormGroup';
import Label from 'reactstrap/lib/Label';
import _values from 'lodash/values';
import { Attribute, getPeeringAttributes, getPeeringAttributesList, Option } from 'utils/peeringAttributesUtils';
import Button from 'reactstrap/lib/Button';
import { hasSomeRole } from 'utils/userUtils';
import { FilteringOptions, PeeringAttributeNames, Roles } from 'constants/literals';
import withUserRoles from 'hocs/withUserRoles';
import { compose } from 'recompose';

interface PeeringAttributesFieldArrayProps {
    name: string;
    attributes: { name: string; value: string }[];
    setFieldValue: (field: keyof FormikValues & string, value: any, shouldValidate?: boolean) => void;
}

export const PeeringAttributesFieldArray = ({ name, attributes, setFieldValue }: PeeringAttributesFieldArrayProps) => {
    const { t } = useTranslation();

    const disableAdding = attributes.some(attr => !attr.name || attr.value === '');

    return (
        <FieldArray
            name={name}
            render={(arrayHelpers) => (
                <>
                    {attributes.map((attribute, index: number) => (
                        <PeeringAttributeForm
                            key={attribute.name}
                            name={`${name}[${index}]`}
                            setFieldValue={setFieldValue}
                            onRemove={() => arrayHelpers.remove(index)}
                            initialAttributeName={attribute.name}
                        />
                    ))}
                    <Button
                        className="text-primary"
                        color="none"
                        onClick={() => arrayHelpers.push({ name: '', value: '' })}
                        disabled={disableAdding}
                    >
                        <i className="fas fa-plus" /> {t('routeServerAddModal.addPeeringAttributeBtn')}
                    </Button>
                </>
            )}
        />
    );
};

interface PeeringAttributeFormProps {
    name: string;
    setFieldValue: (field: keyof FormikValues & string, value: any, shouldValidate?: boolean) => void;
    onRemove?: () => void;
    readonly?: boolean;
    initialAttributeName?: string;
}

export const PeeringAttributeForm = ({
    name,
    setFieldValue,
    onRemove,
    readonly = false,
    initialAttributeName,
}: PeeringAttributeFormProps) => {
    const { t } = useTranslation();
    const [selectedAttribute, setSelectedAttribute] = useState<Attribute | undefined>(undefined);
    const [currentWidth, setCurrentWidth] = useState<number>(window.innerWidth);

    const isSmallScreen = currentWidth < 576;

    const onNameChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedAttributeName = event.target.value;
        setFieldValue(`${name}.name`, selectedAttributeName);
        setFieldValue(`${name}.value`, '');
        const selectedAttribute = getPeeringAttributes(t)[selectedAttributeName];
        setSelectedAttribute(selectedAttribute);
    };

    useEffect(() => {
        if (initialAttributeName) {
            setSelectedAttribute(getPeeringAttributes(t)[initialAttributeName]);
        }
    }, [initialAttributeName, setSelectedAttribute , t]);

    useEffect(() => {
        const handleResize = () => {
            setCurrentWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [setCurrentWidth]);

   

    return (
        <Card className="bg-light">
            <CardBody className="pb-0">
                {!isSmallScreen && (
                    <FormGroup row>
                        <Col sm="5">
                            <Label>{t('common.name')}:</Label>
                            <SelectNameField 
                                name={name}
                                onChange={onNameChange}
                                disabled={readonly}
                            />
                        </Col>

                        <Col sm="5">
                            <Label>{t('common.value')}:</Label>
                            <ValueField
                                name={`${name}.value`}
                                selectedAttribute={selectedAttribute}
                                disabled={readonly}
                            />
                        </Col>
                        <Col sm="2" className="d-flex align-items-end justify-content-center">
                            {!readonly && (
                                <i
                                    className="fas fa-trash text-danger cursor-pointer font-lg btn"
                                    title={t('peering.removeAttribute')}
                                    onClick={onRemove}
                                />
                            )}
                        </Col>
                    </FormGroup>
                )}
                {isSmallScreen && (
                    <>
                        <FormGroup row>
                            <Col xs="2">
                                <Label>{t('common.name')}:</Label>
                            </Col>
                            <Col xs="8">
                            <SelectNameField 
                                name={name}
                                onChange={onNameChange}
                                disabled={readonly}
                            />
                            </Col>
                            <Col xs="1">
                                {!readonly && (
                                    <i
                                        className="fas fa-trash text-danger cursor-pointer font-lg btn"
                                        title={t('peering.removeAttribute')}
                                        onClick={onRemove}
                                    />
                                )}
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col xs="2">
                                <Label>{t('common.value')}:</Label>
                            </Col>
                            <Col xs="8">
                                <ValueField
                                    name={`${name}.value`}
                                    selectedAttribute={selectedAttribute}
                                    disabled={readonly}
                                />
                            </Col>
                        </FormGroup>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

interface SelectNameFieldProps {
    name: string;
    onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    disabled: boolean;
}

const SelectNameField = ({name, onChange, disabled}: SelectNameFieldProps) => {
    const { t } = useTranslation();

    return (
        <Field
            name={`${name}.name`}
            component="select"
            className="form-control"
            onChange={onChange}
            disabled={disabled}
        >
            <option key={''} value={''} disabled>
                {t('common.none')}
            </option>
            {getPeeringAttributesList(t).map((attribute: Attribute) => (
                <option key={attribute.name} value={attribute.name}>
                    {attribute.label}
                </option>
            ))}
        </Field>
    );
};

interface InValueFieldProps {
    name: string;
    selectedAttribute?: Attribute;
    disabled: boolean;
}

interface UserRolesProps {
    userRoles: Roles[];
}

type ValueFieldProps = UserRolesProps & InValueFieldProps;

const ValueField = compose<ValueFieldProps, InValueFieldProps>(withUserRoles)(
    ({ name, selectedAttribute, disabled, userRoles }: ValueFieldProps) => {
        const { t } = useTranslation();

        const optionDisabled = (option: Option) => {
            if (option.disabled) return option.disabled;

            if (selectedAttribute && selectedAttribute.name === PeeringAttributeNames.filtering) {
                return option.option === FilteringOptions.tagOnly && !hasSomeRole(userRoles, Roles.admin, Roles.noc);
            }

            return false;
        };

        return (
            <Field
                type={selectedAttribute ? selectedAttribute.type : 'text'}
                name={name}
                className="form-control"
                placeholder={t('common.value')}
                component={selectedAttribute && selectedAttribute.type === 'select' ? 'select' : 'input'}
                disabled={disabled || !selectedAttribute}
            >
                {selectedAttribute &&
                    selectedAttribute.options &&
                    selectedAttribute.options.map((option: Option) => (
                        <option key={option.option} value={option.option} disabled={optionDisabled(option)}>
                            {option.label}
                        </option>
                    ))}
            </Field>
        );
    }
);
